import { z } from 'zod'
import {
  SP_PASSWORD_RULE_ORDER,
  evaluateSpPasswordRules,
} from '@/features/auth/lib/password-rules'
import { WEB_SURVEY_DEFAULT_PARTY, WEB_SURVEY_REGISTRATION_PARTIES } from './web-survey-registration-type'

const partyEnum = z.enum(WEB_SURVEY_REGISTRATION_PARTIES)

export const WEB_SURVEY_PASSWORD_REQUIREMENTS_ISSUE = 'Password does not meet all requirements'

function passwordRefine(
  data: { password: string; confirmPassword: string },
  ctx: z.RefinementCtx,
): void {
  const checks = evaluateSpPasswordRules(data.password)
  if (!SP_PASSWORD_RULE_ORDER.every((key) => checks[key])) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: WEB_SURVEY_PASSWORD_REQUIREMENTS_ISSUE,
      path: ['confirmPassword'],
    })
  }
  if (data.confirmPassword.length > 0 && data.password !== data.confirmPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    })
  }
}

function nameField(label: string) {
  return z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .min(2, `${label} must be at least 2 characters`)
    .max(50, `${label} must be at most 50 characters`)
}

const webSurveyAccountBaseSchema = z.object({
  firstName: nameField('First name'),
  lastName: nameField('Last name'),
  email: z.string().trim().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  confirmPassword: z.string().min(1, 'Please re-enter your password'),
  registeredPoliticalParty: z.string().optional(),
})

export function buildWebSurveyAccountSchema(registrationTypeEnabled: boolean) {
  return webSurveyAccountBaseSchema
    .superRefine((data, ctx) => {
      passwordRefine(data, ctx)
      if (!registrationTypeEnabled) {
        return
      }
      const parsed = partyEnum.safeParse(data.registeredPoliticalParty)
      if (!parsed.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select an option',
          path: ['registeredPoliticalParty'],
        })
      }
    })
    .transform((data) => {
      if (registrationTypeEnabled) {
        return data
      }
      return {
        ...data,
        registeredPoliticalParty: WEB_SURVEY_DEFAULT_PARTY,
      }
    })
}

export const webSurveyAccountSchema = buildWebSurveyAccountSchema(false)

export type WebSurveyAccountFormData = z.infer<typeof webSurveyAccountBaseSchema>
